import React, { useState } from 'react';
import { triageToNeedScore, scoreToPath, pathLabel } from '../lib/scoring';
import { TRIAGE_QUESTIONS } from '../lib/triageLogic';
import { Icons } from '../lib/Icons';

const DecisionSummary = ({ answers, onConfirm, onOverride, onBack }) => {
  const [showTriageDetails, setShowTriageDetails] = useState(false);
  
  const needScore = triageToNeedScore(answers);
  const recommendedPath = scoreToPath(needScore);

  const getScoreColor = (needScore) => {
    if (needScore <= 2) return 'text-green-600';
    if (needScore <= 6) return 'text-yellow-600';
    return 'text-purple-600';
  };

  const getPathColor = (path) => {
    if (path === 'NC_ONLY') return 'text-green-600';
    if (path === 'NC_WITH_SUPPORTS') return 'text-blue-600';
    if (path === 'BESPOKE') return 'text-purple-600';
    return 'text-yellow-600';
  };

  const getPathIcon = (path) => {
    if (path === 'NC_ONLY') return <Icons.CheckCircle className="w-10 h-10 mx-auto" />;
    if (path === 'NC_WITH_SUPPORTS') return <Icons.Target className="w-10 h-10 mx-auto" />;
    if (path === 'BESPOKE') return <Icons.PaintBrush className="w-10 h-10 mx-auto" />;
    return '🤔';
  };

  const getPathDescription = (path) => {
    if (path === 'NC_ONLY') {
      return 'Access to the national curriculum without additional supports required.';
    }
    if (path === 'NC_WITH_SUPPORTS') {
      return 'Access to the national curriculum with targeted interventions and support strategies.';
    }
    if (path === 'BESPOKE') {
      return 'Custom-designed curriculum tailored to individual needs and learning profile.';
    }
    return 'Either path could be suitable. Professional judgment recommended.';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Triage Assessment Complete</h2>
          <p className="text-gray-600">Review the assessment results and recommended curriculum path</p>
        </div>

        {/* Score Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <Icons.ChartBar className="w-10 h-10 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900 mb-2">Need Score</div>
            <div className={`text-4xl font-bold ${getScoreColor(needScore)}`}>{needScore}/12</div>
          </div>
          
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-4xl font-bold mb-2">{getPathIcon(recommendedPath)}</div>
            <div className="text-2xl font-bold text-gray-900 mb-2">Recommended Path</div>
            <div className={`text-xl font-semibold ${getPathColor(recommendedPath)}`}>
              {pathLabel(recommendedPath)}
            </div>
          </div>
          
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <Icons.ArrowTrendingUp className="w-10 h-10 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900 mb-2">Confidence</div>
            <div className="text-4xl font-bold text-gray-900">
              {recommendedPath === 'EITHER' ? 'Medium' : 'High'}
            </div>
          </div>
        </div>



        {/* Path Details */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommended Path Details</h3>
          <p className="text-gray-700 mb-4">{getPathDescription(recommendedPath)}</p>
          
          {recommendedPath === 'NC_WITH_SUPPORTS' && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">This path includes:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Targeted interventions for specific needs</li>
                <li>Support strategies and accommodations</li>
                <li>Progress monitoring and assessment</li>
                <li>Costed provision mapping</li>
              </ul>
            </div>
          )}
          
          {recommendedPath === 'BESPOKE' && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">This path includes:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Custom learning objectives</li>
                <li>Individualized teaching approaches</li>
                <li>Engagement-focused activities</li>
                <li>Personalized assessment methods</li>
              </ul>
            </div>
          )}
        </div>

        {/* Triage Details Toggle */}
        <div className="mb-8">
          <button
            onClick={() => setShowTriageDetails(!showTriageDetails)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {showTriageDetails ? 'Hide' : 'View'} Triage Answers
            <svg 
              className={`w-4 h-4 ml-2 transform transition-transform ${showTriageDetails ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Triage Details */}
        {showTriageDetails && (
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Triage Assessment Details</h3>
            <div className="space-y-4">
              {TRIAGE_QUESTIONS.map((question, index) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{question}</h4>
                    <span className="text-2xl font-bold text-indigo-600">{answers[index]}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {answers[index] === 0 && "Significant challenge: cannot participate meaningfully without bespoke tasks or high support"}
                    {answers[index] === 1 && "Some challenge: requires regular scaffold, adjustments and adult prompts"}
                    {answers[index] === 2 && "Minimal/none: can access with typical classroom support"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Triage
          </button>

          <div className="flex space-x-4">
            <button
              onClick={onOverride}
              className="inline-flex items-center px-4 py-2 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Override Recommendation
            </button>
            
            <button
              onClick={onConfirm}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Confirm Path
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionSummary;
